import { signInWithEmailAndPassword } from "firebase/auth";
import { useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { useAuth } from "../../auth/AuthProvider";
import { Button } from "../../components/Button";
import { FormAlert, SecretField, TextField, focusFirstInvalid } from "../../components/fields";
import { useDocumentTitle } from "../../components/layout";
import { authErrorMessage } from "../../lib/firebase";
import { rememberNext, safeNext, takeNext, withNext } from "../guards";
import { AuthLayout } from "./AuthLayout";

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoginPage() {
  useDocumentTitle("Sign in");
  const { auth, user, initializing } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const reason = params.get("reason");
  const nextParam = params.get("next");

  if (!initializing && user && !busy) {
    return <Navigate to={user.emailVerified ? safeNext(nextParam, takeNext()) : withNext("/verify-email", nextParam)} replace />;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const nextErrors: typeof errors = {};
    if (!email.trim()) nextErrors.email = "Enter your email address.";
    else if (!EMAIL_RE.test(email.trim())) nextErrors.email = "Enter a valid email address, like sam@example.com.";
    if (!password) nextErrors.password = "Enter your password.";
    setErrors(nextErrors);
    setFormError(null);
    if (Object.keys(nextErrors).length) {
      focusFirstInvalid(formRef.current);
      return;
    }
    setBusy(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      if (nextParam) rememberNext(safeNext(nextParam));
      navigate(cred.user.emailVerified ? safeNext(nextParam, takeNext()) : withNext("/verify-email", nextParam), { replace: true });
    } catch (error) {
      setPassword("");
      setFormError(authErrorMessage(error));
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      lead="Sign in to your household's kitchen."
      footer={
        <p>
          New to Supperkeep? <Link to={`/register${nextParam ? `?next=${encodeURIComponent(nextParam)}` : ""}`}>Create an account</Link>
        </p>
      }
    >
      {reason === "expired" ? <FormAlert tone="warning">Your session expired. Sign in again to pick up where you left off.</FormAlert> : null}
      {reason === "deleted" ? <FormAlert tone="info">Your account and its data were deleted.</FormAlert> : null}
      {params.get("reset") === "1" ? <FormAlert tone="success">Password changed. Sign in with your new password.</FormAlert> : null}
      <form ref={formRef} noValidate onSubmit={submit} className="stack">
        <FormAlert>{formError}</FormAlert>
        <TextField label="Email" type="email" autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck={false} value={email} error={errors.email} onChange={(e) => setEmail(e.target.value)} required />
        <SecretField label="Password" autoComplete="current-password" value={password} error={errors.password} onChange={(e) => setPassword(e.target.value)} required />
        <div className="auth-row">
          <Link to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`}>Forgot your password?</Link>
        </div>
        <Button type="submit" emphasis="solid" intent="brand" size="lg" block busy={busy}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
